/**
 * Shared Approval Validation Schemas
 *
 * Zod validation schemas for approval workflow forms.
 * Used by both Documents and Record Types for consistent validation.
 */

import {z} from 'zod';

/**
 * Submit for approval form schema
 *
 * Used when submitting a resource for approval.
 * Validates approver selection and optional comment.
 */
export const submitApprovalSchema = z.object({
  /** At least one approver must be selected */
  approverIds: z
    .array(z.string().uuid())
    .min(1, 'At least one approver is required'),

  /** Approval rule (always 'all' for MVP) */
  approvalRule: z.enum(['all', 'any']).default('all'),

  /** Optional comment about submission */
  comment: z.string().optional(),
});

export type SubmitApprovalFormData = z.infer<typeof submitApprovalSchema>;

/**
 * Approval decision form schema (rejection only)
 *
 * This schema is only used by the shared ApprovalDecisionDialog, which
 * in practice only handles rejections — approvals use domain-specific
 * dialogs (SignatureModal with password re-auth).
 */
export const approvalDecisionSchema = z.object({
  /** Decision type */
  decision: z.enum(['approved', 'rejected']),

  /** Comment explaining the decision (REQUIRED) */
  comment: z.string().min(1, 'Comment is required'),
});

export type ApprovalDecisionFormData = z.infer<typeof approvalDecisionSchema>;

/**
 * Reassign approvers form schema
 *
 * Used when reassigning approvers.
 * Validates new approver selection and optional comment.
 */
export const reassignApproversSchema = z.object({
  /** New list of approvers (replaces existing) */
  approverIds: z
    .array(z.string().uuid())
    .min(1, 'At least one approver is required'),

  /** Optional comment about reassignment */
  comment: z.string().optional(),
});

export type ReassignApproversFormData = z.infer<typeof reassignApproversSchema>;
